<template>
  <div class="scanner-container">
    <div v-if="loading" class="loading-section">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Scanning folder structure...</p>
        <p style="color: #666; font-size: 14px;">Analyzing documents and identifying types</p>
      </div>
    </div>

    <div v-else>
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="card">
          <div class="card-number" style="color: #409eff;">
            {{ scanResults?.total_draws || 0 }}
          </div>
          <div class="card-label">Total Draws</div>
        </div>
        
        <div class="card">
          <div class="card-number" style="color: #67c23a;">
            {{ scanResults?.total_documents || 0 }}
          </div>
          <div class="card-label">Total Documents</div>
        </div>
        
        <div class="card">
          <div class="card-number" style="color: #e6a23c;">
            {{ scanResults?.document_types?.length || 0 }}
          </div>
          <div class="card-label">Document Types</div>
        </div>
      </div>

      <!-- CSV Project Metadata -->
      <div v-if="scanResults?.csv_files?.length > 0" class="info-card">
        <h4>Project Metadata Found</h4>
        
        <p><strong>CSV File:</strong> {{ scanResults.csv_files[0].filename }}</p>
        <div v-if="scanResults.project_metadata && Object.keys(scanResults.project_metadata).length > 0">
          <p><strong>Extracted Project Info:</strong></p>
          <ul>
            <li v-if="scanResults.project_metadata.name">
              <strong>Name:</strong> {{ scanResults.project_metadata.name }}
            </li>
            <li v-if="scanResults.project_metadata.project_number">
              <strong>Project Number:</strong> {{ scanResults.project_metadata.project_number }}
            </li>
            <li v-if="scanResults.project_metadata.address_line_1">
              <strong>Address:</strong> {{ scanResults.project_metadata.address_line_1 }}
            </li>
            <li v-if="scanResults.project_metadata.total_original_budget">
              <strong>Total Budget:</strong> ${{ scanResults.project_metadata.total_original_budget.toLocaleString() }}
            </li>
            <li v-if="scanResults.project_metadata.general_contractor">
              <strong>General Contractor:</strong> {{ scanResults.project_metadata.general_contractor }}
            </li>
          </ul>
          <div class="success-alert">
            ✅ This information will auto-populate your project details
          </div>
        </div>
      </div>

      <!-- Document Types Summary -->
      <div class="info-card">
        <h4>Document Types Found</h4>
        
        <div class="doc-types">
          <div 
            v-for="type in documentTypesSummary" 
            :key="type.type"
            class="doc-type-badge"
          >
            <span class="doc-icon">📄</span>
            <span class="doc-text">
              {{ type.type.toUpperCase() }}: {{ type.count }}
            </span>
          </div>
        </div>
      </div>


      <!-- Template Setup Section (Top Priority) -->
      <div v-if="inconsistentFiles.length > 0" class="template-setup-section">
        <div class="setup-header">
          <h4>🔧 Excel Template Setup Required</h4>
          <div class="setup-stats">
            <span class="stat-badge warning">{{ inconsistentFiles.length }} files need templates</span>
            <span v-if="templatesCompleted > 0" class="stat-badge success">{{ templatesCompleted }} completed</span>
            <span v-if="templatesInProgress > 0" class="stat-badge info">{{ templatesInProgress }} in progress</span>
          </div>
        </div>
        
        <p class="setup-description">
          These Excel files have different column layouts and need individual template configuration. 
          Click "Setup Template" for each file to configure column mappings.
        </p>
        
        <!-- Template Setup Grid -->
        <div class="template-setup-grid">
          <div v-for="file in inconsistentFiles" :key="file.filename" class="template-card">
            <div class="template-card-header">
              <div class="file-info">
                <span class="file-name">{{ file.filename }}</span>
                <span class="file-details">Draw {{ file.draw_number }} • {{ getColumnPattern(file) }}</span>
              </div>
              <span class="status-badge" :class="getTemplateStatusClass(file)">
                {{ getTemplateStatusLabel(file) }}
              </span>
            </div>
            
            <div v-if="file.format_differences" class="format-differences">
              <small>{{ file.format_differences.join(', ') }}</small>
            </div>
            
            <button 
              class="template-setup-btn" 
              @click="openTemplateSetup(file)"
              :disabled="file.template_status === 'setup_initiated' || file.template_status === 'completed' || file.template_status === 'compatible'"
            >
              {{ getTemplateButtonText(file) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Excel Analysis Summary -->
      <div v-if="scanResults?.excel_analysis" class="info-card">
        <h4>📊 Excel Files Analysis</h4>
        
        <div class="analysis-summary">
          <div class="summary-row">
            <span class="label">Total Excel Files:</span>
            <span class="value">{{ scanResults.excel_analysis.total_excel_files }}</span>
          </div>
          <div class="summary-row">
            <span class="label">Sample File:</span>
            <span class="value">{{ scanResults.excel_analysis.sample_file }}</span>
          </div>
        </div>
        
        <!-- Consistency Summary -->
        <div v-if="columnConsistencyAnalysis" class="consistency-summary-compact">
          <div class="consistency-stat" :class="{ 'success': consistentFiles.length > 0 }">
            <span class="stat-number">{{ consistentFiles.length }}</span>
            <span class="stat-label">Consistent Files</span>
          </div>
          <div class="consistency-stat" :class="{ 'warning': inconsistentFiles.length > 0 }">
            <span class="stat-number">{{ inconsistentFiles.length }}</span>
            <span class="stat-label">Need Templates</span>
          </div>
        </div>
        
        <!-- Consistent Files (Collapsed) -->
        <div v-if="consistentFiles.length > 0" class="consistent-files-compact">
          <h6>✅ Template-Ready Files</h6>
          <div class="file-list-compact">
            <span v-for="(file, index) in consistentFiles" :key="file.filename" class="file-tag">
              {{ file.filename }}{{ index < consistentFiles.length - 1 ? ', ' : '' }}
            </span>
          </div>
          <p class="help-text-small">These files can use a single bulk template configuration.</p>
        </div>
      </div>

      <!-- Draws Table -->
      <div class="info-card">
        <h4>Draws Found</h4>
        
        <div class="draws-table">
          <table>
            <thead>
              <tr>
                <th>Draw #</th>
                <th>Files</th>
                <th>Document Types</th>
                <th>Files Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="draw in scanResults?.draws || []" :key="draw.number">
                <td><strong>Draw {{ draw.number }}</strong></td>
                <td>{{ draw.files.length }}</td>
                <td>
                  <div class="type-tags">
                    <span 
                      v-for="type in getUniqueTypes(draw.files)" 
                      :key="type"
                      class="type-tag"
                      :class="getFileTypeClass(type)"
                    >
                      {{ type.toUpperCase() }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="file-list">
                    <div 
                      v-for="(file, index) in draw.files" 
                      :key="index"
                      class="file-item"
                    >
                      {{ file.filename }} ({{ file.size_mb }}MB)
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="continue-section">
        <!-- Template Setup Required -->
        <div v-if="inconsistentFiles.length > 0" class="template-setup-required">
          <div class="setup-status">
            <h5>📝 Template Setup Required</h5>
            <p>{{ inconsistentFiles.length }} files need individual template configuration. Set up templates using the "📝 Setup Template" buttons above.</p>
          </div>
          
          <!-- Template Setup Progress -->
          <div v-if="templateSetupStarted" class="setup-progress">
            <div class="progress-stats">
              <span class="stat">
                <span class="stat-number">{{ templatesCompleted }}</span>
                <span class="stat-label">Templates Completed</span>
              </span>
              <span class="stat">
                <span class="stat-number">{{ templatesInProgress }}</span>
                <span class="stat-label">Setup In Progress</span>
              </span>
              <span class="stat">
                <span class="stat-number">{{ templatesRemaining }}</span>
                <span class="stat-label">Remaining</span>
              </span>
            </div>
          </div>
          
          <!-- Continue Options -->
          <div class="continue-options">
            <button 
              v-if="templatesCompleted > 0 || !templateSetupStarted"
              class="continue-btn"
              @click="continueToNextStep"
            >
              Continue to Configuration
            </button>
            <button 
              v-if="templatesRemaining > 0"
              class="skip-remaining-btn"
              @click="skipRemainingTemplates"
            >
              Skip Remaining Templates
            </button>
          </div>
          
          <p class="help-text">
            <strong>Note:</strong> Files without templates will need individual configuration later via Project Documents.
          </p>
        </div>
        
        <!-- Normal Continue (No inconsistent files) -->
        <div v-else class="normal-continue">
          <button 
            class="continue-btn"
            @click="continueToNextStep"
          >
            Continue to Configuration
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  projectImport: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['complete', 'project-created'])

const loading = ref(true)
const scanResults = ref(null)
const columnConsistencyAnalysis = ref(null)
const projectId = ref(null)

const documentTypesSummary = computed(() => {
  if (!scanResults.value?.document_types) return []
  
  return scanResults.value.document_types.map(type => ({
    type,
    count: scanResults.value.draws.reduce((sum, draw) => 
      sum + draw.files.filter(f => f.type === type).length, 0
    )
  }))
})

// Column consistency analysis computed properties
const consistentFiles = computed(() => {
  if (!columnConsistencyAnalysis.value?.files) return []
  return columnConsistencyAnalysis.value.files.filter(file => file.is_consistent)
})

const inconsistentFiles = computed(() => {
  if (!columnConsistencyAnalysis.value?.files) return []
  return columnConsistencyAnalysis.value.files.filter(file => !file.is_consistent)
})

const commonPatterns = computed(() => {
  if (!columnConsistencyAnalysis.value?.patterns) return []
  return columnConsistencyAnalysis.value.patterns
})

// Template setup progress tracking
const templateSetupStarted = computed(() => {
  return inconsistentFiles.value.some(file => file.template_status && file.template_status !== 'needs_template')
})

const templatesCompleted = computed(() => {
  return inconsistentFiles.value.filter(file => file.template_status === 'completed' || file.template_status === 'compatible').length
})

const templatesInProgress = computed(() => {
  return inconsistentFiles.value.filter(file => file.template_status === 'setup_initiated').length
})

const templatesRemaining = computed(() => {
  return inconsistentFiles.value.filter(file => !file.template_status || file.template_status === 'needs_template').length
})

const scanFolder = async () => {
  try {
    const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/scan_folder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })

    const data = await response.json()
    
    if (data.success) {
      scanResults.value = data.scan_results
      
      // Store project_id if project was created during scanning
      if (data.project_id) {
        projectId.value = data.project_id
        emit('project-created', data.project_id)
        console.log('📋 Project created during scanning! Project ID:', data.project_id)
      } else {
        console.log('⚠️ No project was created during scanning - CSV metadata may be missing')
      }
      
      // Run Excel column consistency analysis if Excel files found
      if (data.scan_results.excel_analysis?.total_excel_files > 0) {
        await analyzeExcelConsistency()
      }
      
      let message = `Scan completed! Found ${data.scan_results.total_draws} draws with ${data.scan_results.total_documents} documents.`
      if (data.project_created) {
        message += `\n\n✅ Project created automatically from CSV metadata!`
      }
      alert(message)
      
      // Note: No auto-advance - users must handle template setup if needed
    } else {
      throw new Error(data.error || 'Failed to scan folder')
    }
  } catch (error) {
    alert(`Scan failed: ${error.message}`)
  } finally {
    loading.value = false
  }
}

const analyzeExcelConsistency = async () => {
  try {
    console.log('🔍 Analyzing Excel column consistency across all files...')
    
    // Get consistency analysis first
    const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/analyze_excel_consistency`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })

    const data = await response.json()
    
    if (data.success) {
      // For new projects, mark ALL files as needing template setup initially
      if (data.consistency_analysis?.files) {
        console.log('📋 New project - marking all files as needing template setup')
        data.consistency_analysis.files.forEach(file => {
          file.is_consistent = false
          file.template_status = 'needs_template'
          
          // Log the file's pattern signature for debugging
          const filePattern = typeof file.column_structure === 'string' ? 
            { structure: file.column_structure } : 
            file.column_structure?.pattern_signature || { structure: file.column_structure }
          
          console.log(`📄 File needs template: ${file.filename}`, {
            pattern: filePattern.structure
          })
        })
      }
      
      columnConsistencyAnalysis.value = data.consistency_analysis
      console.log('✅ Excel consistency analysis completed:', {
        totalFiles: data.consistency_analysis?.files?.length || 0,
        allNeedTemplates: true,
        message: 'New project - all files need template setup'
      })
    } else {
      console.warn('⚠️ Excel consistency analysis failed:', data.error)
      // Create fallback analysis from scan results
      columnConsistencyAnalysis.value = createFallbackConsistencyAnalysis()
    }
  } catch (error) {
    console.error('❌ Error during Excel consistency analysis:', error)
    // Create fallback analysis
    columnConsistencyAnalysis.value = createFallbackConsistencyAnalysis()
  }
}

const createFallbackConsistencyAnalysis = () => {
  // Create a basic consistency analysis from available data
  const excelFiles = getAllExcelFiles()
  
  // Simple heuristic: files with similar names are likely consistent
  const patterns = groupFilesByPattern(excelFiles)
  
  return {
    total_files: excelFiles.length,
    consistent_files: patterns.length > 0 ? patterns[0].files.length : 0,
    inconsistent_files: excelFiles.length - (patterns.length > 0 ? patterns[0].files.length : 0),
    files: excelFiles.map(file => ({
      filename: file.filename,
      draw_number: file.draw_number,
      is_consistent: isLikelyConsistent(file, excelFiles),
      column_count: estimateColumnCount(file),
      format_differences: getFormatDifferences(file, excelFiles)
    })),
    patterns: patterns
  }
}

const getAllExcelFiles = () => {
  if (!scanResults.value?.draws) return []
  
  return scanResults.value.draws.flatMap(draw => 
    draw.files.filter(file => file.type === 'excel').map(file => ({
      ...file,
      draw_number: draw.number
    }))
  )
}

const groupFilesByPattern = (files) => {
  // Group files by naming patterns and estimated structure
  const patterns = {}
  
  files.forEach(file => {
    const pattern = getFilePattern(file)
    if (!patterns[pattern]) {
      patterns[pattern] = {
        description: pattern,
        files: []
      }
    }
    patterns[pattern].files.push(file.filename)
  })
  
  return Object.values(patterns).sort((a, b) => b.files.length - a.files.length)
}

const getFilePattern = (file) => {
  const name = file.filename.toLowerCase()
  
  // Detect pattern based on filename
  if (name.includes('draw') && name.match(/draw\s*\d+/)) {
    return 'Standard Draw Format'
  } else if (name.includes('madison') || name.includes('development')) {
    return 'Madison Development Format'
  } else if (name.includes('summary') || name.includes('total')) {
    return 'Summary Format'
  } else {
    return 'Custom Format'
  }
}

const isLikelyConsistent = (file, allFiles) => {
  // Simple heuristic: if filename follows a pattern used by multiple files
  const pattern = getFilePattern(file)
  const samePatternCount = allFiles.filter(f => getFilePattern(f) === pattern).length
  return samePatternCount >= 2
}

const estimateColumnCount = (file) => {
  // Estimate based on file size and type
  const sizeInMB = parseFloat(file.size_mb) || 0
  if (sizeInMB > 5) return '20+ columns'
  if (sizeInMB > 2) return '10-20 columns'
  return '5-10 columns'
}

const getFormatDifferences = (file, allFiles) => {
  const differences = []
  const pattern = getFilePattern(file)
  
  if (pattern === 'Custom Format') {
    differences.push('Unique naming pattern')
  }
  
  const sizeInMB = parseFloat(file.size_mb) || 0
  const avgSize = allFiles.reduce((sum, f) => sum + (parseFloat(f.size_mb) || 0), 0) / allFiles.length
  
  if (Math.abs(sizeInMB - avgSize) > avgSize * 0.5) {
    differences.push('Significantly different file size')
  }
  
  return differences
}

const getColumnPattern = (file) => {
  if (file.column_structure) {
    return file.column_structure
  }
  return `${file.column_count || estimateColumnCount(file)} • ${getFilePattern(file)}`
}

const openTemplateSetup = async (file) => {
  try {
    console.log('🔧 Setting up template for file:', file.filename)
    console.log('🔧 File path:', file.path)
    
    // Mark this file as having template setup initiated
    const fileIndex = inconsistentFiles.value.findIndex(f => f.filename === file.filename)
    if (fileIndex !== -1) {
      inconsistentFiles.value[fileIndex].template_status = 'setup_initiated'
    }
    
    // Create a new Excel template using the WORKING backend API from ProjectSummary.vue
    // Debug the projectImport object to find the correct project ID field
    console.log('🔧 ProjectImport object:', props.projectImport)
    console.log('🔧 ProjectImport keys:', Object.keys(props.projectImport))
    
    // Use the project ID from scanning or try to find it in projectImport
    // DO NOT use props.projectImport.id as that's the IMPORT ID, not PROJECT ID!
    const resolvedProjectId = projectId.value || 
                              props.projectImport.project_id || 
                              props.projectImport.projectId || 
                              props.projectImport.Project?.id
    
    console.log('🔧 Resolved project ID:', resolvedProjectId)
    console.log('🔧 ProjectImport ID (NOT project ID):', props.projectImport.id)
    
    if (!resolvedProjectId) {
      throw new Error('No project found! Please scan the folder first to create a project from CSV metadata. Project import ID ' + props.projectImport.id + ' has no associated project.')
    }
    
    console.log('🔧 Reading Excel file from path:', file.path)
    
    // Read the Excel file from the filesystem
    let fileContent
    try {
      const fileResponse = await fetch(`/api/v1/project_imports/${props.projectImport.id}/read_file?file_path=${encodeURIComponent(file.path)}`)
      if (!fileResponse.ok) {
        throw new Error(`Failed to read file: ${fileResponse.statusText}`)
      }
      const fileBlob = await fileResponse.blob()
      fileContent = new File([fileBlob], file.filename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      console.log('✅ File read successfully:', file.filename, fileContent.size, 'bytes')
    } catch (fileError) {
      console.error('❌ Failed to read file:', fileError)
      throw new Error(`Could not read Excel file from ${file.path}: ${fileError.message}`)
    }
    
    // Determine template type based on filename
    const isLenderFile = file.filename.toLowerCase().includes('lender')
    const templateType = isLenderFile ? 'contractor_budget_tracker' : 'excel_draw_tracker'
    const templateName = isLenderFile ? 
      `Contractor Budget Template - ${file.filename}` : 
      `Excel Draw Template - ${file.filename}`
    
    const templateData = {
      name: templateName,
      description: `Auto-generated ${isLenderFile ? 'contractor/lender' : 'draw'} template for ${file.filename}`,
      draw_number: isLenderFile ? null : (file.draw_number || 'import'),
      project_id: resolvedProjectId,
      template_type: templateType
    }
    
    console.log('🔧 Creating template with Excel file upload:', templateData)
    
    // Create FormData for file upload with template
    const formData = new FormData()
    formData.append('document[file]', fileContent)
    formData.append('document[project_id]', resolvedProjectId)
    formData.append('document[document_type]', templateType)
    formData.append('document[draw_number]', isLenderFile ? null : (file.draw_number || 'import'))
    formData.append('draw_template[name]', templateData.name)
    formData.append('draw_template[description]', templateData.description)
    formData.append('draw_template[template_type]', templateData.template_type)
    formData.append('draw_template[project_id]', resolvedProjectId)
    formData.append('draw_template[draw_number]', isLenderFile ? null : (file.draw_number || 'import'))
    
    // Create template WITH Excel file upload
    const response = await fetch('/api/v1/draw_templates', {
      method: 'POST',
      body: formData
    })
    
    const data = await response.json()
    
    if (data.success && data.template) {
      console.log('✅ Template created successfully:', data.template.id)
      
      // Navigate to the WORKING template setup page
      const templateSetupUrl = `/draw-template-setup/${data.template.id}?filename=${encodeURIComponent(file.filename)}&file_path=${encodeURIComponent(file.path)}&return_to=project_import_scanner&template_type=${templateType}`
      console.log('🔧 Opening template setup URL:', templateSetupUrl)
      
      // Open in new window so we can receive completion message and stay on scanner
      const templateWindow = window.open(templateSetupUrl, '_blank', 'width=1400,height=900,scrollbars=yes,resizable=yes')
      
      if (!templateWindow) {
        alert('Popup blocked! Please allow popups and try again, or navigate manually to the template setup page.')
        // Fallback to same window if popup blocked
        window.location.href = templateSetupUrl
      }
      
    } else {
      throw new Error(data.error || 'Failed to create template')
    }
    
  } catch (error) {
    console.error('Error setting up template:', error)
    
    // Reset template status on error
    const fileIndex = inconsistentFiles.value.findIndex(f => f.filename === file.filename)
    if (fileIndex !== -1) {
      inconsistentFiles.value[fileIndex].template_status = 'needs_template'
    }
    
    // Simple fallback
    alert(`Template Setup Failed for: ${file.filename}\n\nError: ${error.message}\n\n` +
          `Please set up manually:\n` +
          `1. Go to Project Documents\n` +
          `2. Click "Upload Document"\n` +
          `3. Select "${file.filename}" from:\n   ${file.path}\n` +
          `4. Choose "Excel Draw Tracker" template\n` +
          `5. Configure column mappings`)
  }
}

const getFileTypeClass = (type) => {
  const classes = {
    excel: 'type-excel',
    g702: 'type-g702',
    g703: 'type-g703',
    lien_waiver: 'type-lien',
    sub_g702: 'type-sub',
    pdf_other: 'type-pdf'
  }
  return classes[type] || 'type-default'
}

const getUniqueTypes = (files) => {
  const types = files.map(f => f.type)
  return [...new Set(types)]
}

const skipRemainingTemplates = () => {
  if (confirm(`Skip remaining template setup?\n\n` +
              `${templatesRemaining.value} files still need template configuration.\n\n` +
              `You can set them up later via Project Documents.\n\n` +
              `Continue anyway?`)) {
    
    // Mark all remaining files as 'skipped' so they don't block progression
    inconsistentFiles.value.forEach(file => {
      if (!file.template_status || file.template_status === 'needs_template') {
        file.template_status = 'skipped'
        console.log(`📋 Marking ${file.filename} as skipped`)
      }
    })
    
    // Also update the consistency analysis data
    if (columnConsistencyAnalysis.value?.files) {
      columnConsistencyAnalysis.value.files.forEach(file => {
        if (!file.template_status || file.template_status === 'needs_template') {
          file.template_status = 'skipped'
        }
      })
    }
    
    console.log('📋 All remaining templates marked as skipped, proceeding to next step')
    continueToNextStep()
  }
}

const continueToNextStep = () => {
  // Include consistency analysis in the scan results
  const enhancedScanResults = {
    ...scanResults.value,
    consistency_analysis: columnConsistencyAnalysis.value
  }
  emit('complete', enhancedScanResults)
}

const getTemplateStatusClass = (file) => {
  switch (file.template_status) {
    case 'setup_initiated':
      return 'info'
    case 'completed':
      return 'success'
    case 'compatible':
      return 'success'
    default:
      return 'warning'
  }
}

const getTemplateStatusLabel = (file) => {
  switch (file.template_status) {
    case 'setup_initiated':
      return '🔧 Template Setup Started'
    case 'completed':
      return '✅ Template Ready'
    case 'compatible':
      return `🔗 Uses Template (${file.compatible_with || 'Compatible'})`
    default:
      return '⚙️ Needs Template'
  }
}

const getTemplateButtonText = (file) => {
  switch (file.template_status) {
    case 'setup_initiated':
      return '🔧 Setup In Progress...'
    case 'completed':
      return '✅ Template Complete'
    case 'compatible':
      return '🔗 Template Available'
    default:
      return '📝 Setup Template'
  }
}

// Listen for template completion events
const handleTemplateComplete = (event) => {
  if (event.data.type === 'template_complete') {
    console.log('📝 Template completed:', event.data)
    
    // Find the completed file to get its pattern
    const completedFileIndex = inconsistentFiles.value.findIndex(f => f.filename === event.data.filename)
    let completedFile = null
    
    if (completedFileIndex !== -1) {
      completedFile = inconsistentFiles.value[completedFileIndex]
      completedFile.template_status = 'completed'
      completedFile.template_id = event.data.template_id
      
      // Update the underlying consistency analysis data as well
      if (columnConsistencyAnalysis.value?.files) {
        const analysisFileIndex = columnConsistencyAnalysis.value.files.findIndex(f => f.filename === event.data.filename)
        if (analysisFileIndex !== -1) {
          columnConsistencyAnalysis.value.files[analysisFileIndex].template_status = 'completed'
          columnConsistencyAnalysis.value.files[analysisFileIndex].template_id = event.data.template_id
        }
      }
    }
    
    // Now check other files against this newly created template
    if (completedFile && completedFile.column_structure) {
      // Get the completed template's pattern signature
      const completedPattern = typeof completedFile.column_structure === 'string' ? 
        { structure: completedFile.column_structure } : 
        completedFile.column_structure.pattern_signature || { structure: completedFile.column_structure }
      
      // Get the template type from the event data
      const completedTemplateType = event.data.template_type
      const isContractorTemplate = completedTemplateType === 'contractor_budget_tracker'
      
      console.log(`🔄 Checking files against new template:`, {
        filename: event.data.filename,
        type: completedTemplateType,
        structure: completedPattern.structure,
        isContractorTemplate
      })
      
      let compatibleCount = 0
      
      // Check all files for compatibility based on template structure
      inconsistentFiles.value.forEach(file => {
        // Skip if file is already processed
        if (file.filename !== event.data.filename && 
            (!file.template_status || file.template_status === 'needs_template')) {
          
          // Check if file is a contractor/lender file
          const isFileContractorType = file.filename.toLowerCase().includes('lender') || 
                                     file.filename.toLowerCase().includes('contractor') ||
                                     file.filename.toLowerCase().includes('budget')
          
          // For contractor templates, only match other contractor files
          // For draw templates, only match non-contractor files
          if (isContractorTemplate === isFileContractorType) {
            // Get file's pattern signature
            const filePattern = typeof file.column_structure === 'string' ? 
              { structure: file.column_structure } : 
              file.column_structure?.pattern_signature || { structure: file.column_structure }
            
            // Check if file matches template structure
            const isStructureMatch = filePattern.structure === completedPattern.structure
            
            console.log(`🔍 Checking ${file.filename}:`, {
              structure: filePattern.structure,
              structureMatch: isStructureMatch,
              isContractorType: isFileContractorType
            })
            
            // Match if structure matches and file type is appropriate
            if (isStructureMatch) {
              console.log(`✅ Marking ${file.filename} as compatible with ${event.data.filename}`)
              file.template_status = 'compatible'
              file.template_id = event.data.template_id
              file.compatible_with = event.data.filename
              compatibleCount++
              
              // Update consistency analysis to mark as consistent
              if (columnConsistencyAnalysis.value?.files) {
                const analysisFile = columnConsistencyAnalysis.value.files.find(f => f.filename === file.filename)
                if (analysisFile) {
                  analysisFile.is_consistent = true
                  analysisFile.template_status = 'compatible'
                  analysisFile.template_id = event.data.template_id
                }
              }
            } else {
              console.log(`❌ ${file.filename} structure does not match template`)
            }
          } else {
            console.log(`⚠️ Skipping ${isContractorTemplate ? 'non-contractor' : 'contractor'} file: ${file.filename}`)
          }
        }
      })
      
      console.log(`✅ Found ${compatibleCount} files compatible with new template`)
    }
  }
}

onMounted(() => {
  scanFolder()
  
  // Listen for messages from template setup windows
  window.addEventListener('message', handleTemplateComplete)
})

onUnmounted(() => {
  window.removeEventListener('message', handleTemplateComplete)
})
</script>

<style scoped>
.scanner-container {
  padding: 20px 0;
}

/* Template Setup Section Styles */
.template-setup-section {
  background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
  border: 3px solid #ff9800;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2);
}

.setup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.setup-header h4 {
  margin: 0;
  color: #e65100;
  font-size: 18px;
}

.setup-stats {
  display: flex;
  gap: 10px;
}

.stat-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.stat-badge.warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.stat-badge.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.stat-badge.info {
  background: #cce5ff;
  color: #004085;
  border: 1px solid #b3d7ff;
}

.setup-description {
  margin: 0 0 20px 0;
  color: #666;
  font-size: 14px;
}

.template-setup-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 15px;
}

.template-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.template-card:hover {
  box-shadow: 0 6px 16px rgba(0,0,0,0.15);
  border-color: #ff9800;
  transform: translateY(-2px);
}

.template-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.file-info {
  flex: 1;
}

.file-name {
  display: block;
  font-weight: bold;
  color: #333;
  font-size: 14px;
  margin-bottom: 4px;
}

.file-details {
  display: block;
  font-size: 12px;
  color: #666;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
}

.status-badge.warning {
  background: #fff3cd;
  color: #856404;
}

.status-badge.info {
  background: #cce5ff;
  color: #004085;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
}

.format-differences {
  margin-bottom: 10px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 11px;
  color: #666;
}

.template-setup-btn {
  width: 100%;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border: none;
  padding: 12px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.3);
}

.template-setup-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.template-setup-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Compact Analysis Styles */
.analysis-summary {
  margin-bottom: 15px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.summary-row .label {
  font-weight: bold;
  color: #333;
}

.summary-row .value {
  color: #666;
}

.consistency-summary-compact {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.consistency-summary-compact .consistency-stat {
  flex: 1;
  padding: 10px;
  background: white;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e9ecef;
}

.consistency-summary-compact .consistency-stat.success {
  border-color: #28a745;
  background: #f8fff9;
}

.consistency-summary-compact .consistency-stat.warning {
  border-color: #ffc107;
  background: #fffdf5;
}

.consistency-summary-compact .stat-number {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.consistency-summary-compact .stat-label {
  display: block;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}

.consistent-files-compact h6 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 14px;
}

.file-list-compact {
  margin-bottom: 8px;
}

.file-tag {
  font-size: 12px;
  color: #666;
}

.help-text-small {
  margin: 0;
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.file-card.consistent {
  border-left: 4px solid #28a745;
}

.file-card.inconsistent {
  border-left: 4px solid #ffc107;
}

.file-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.file-name {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.consistency-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.consistency-badge.success {
  background: #d4edda;
  color: #155724;
}

.consistency-badge.warning {
  background: #fff3cd;
  color: #856404;
}

.consistency-badge.info {
  background: #d1ecf1;
  color: #0c5460;
}

.column-info {
  margin-bottom: 10px;
}

.column-pattern {
  color: #666;
  font-size: 12px;
}

.differences {
  margin-top: 5px;
}

.differences small {
  color: #dc3545;
  font-style: italic;
}

.template-setup-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s ease;
  width: 100%;
}

.template-setup-btn:hover:not(:disabled) {
  background: #0056b3;
}

.template-setup-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

.help-text {
  color: #666;
  font-size: 12px;
  margin: 0;
  font-style: italic;
}

.patterns-section {
  margin-top: 20px;
}

.patterns-section h6 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 14px;
}

.pattern-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pattern-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
}

.pattern-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.pattern-name {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.pattern-count {
  background: #e9ecef;
  color: #495057;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.column-structure {
  color: #666;
  font-size: 12px;
}

.example-files {
  margin-top: 5px;
}

.example-files small {
  color: #999;
  font-size: 11px;
}

.loading-section {
  text-align: center;
  padding: 40px 0;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.summary-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.card {
  flex: 1;
  text-align: center;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
}

.card-number {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
}

.card-label {
  color: #666;
  font-size: 14px;
}

.info-card {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.info-card h4 {
  margin: 0 0 16px 0;
  color: #4A5FD5;
  font-size: 16px;
}

.info-card ul {
  margin: 0;
  padding-left: 20px;
}

.success-alert {
  background: #f0f9ff;
  color: #52c41a;
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 14px;
}

.doc-types {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.doc-type-badge {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
}

.doc-icon {
  margin-right: 8px;
}

.doc-text {
  font-weight: bold;
}

.draws-table {
  max-height: 400px;
  overflow-y: auto;
}

.draws-table table {
  width: 100%;
  border-collapse: collapse;
}

.draws-table th,
.draws-table td {
  border: 1px solid #eee;
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.draws-table th {
  background: #f5f5f5;
  font-weight: bold;
}

.type-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.type-tag {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: bold;
}

.type-excel { background: #d4edda; color: #155724; }
.type-g702 { background: #cce5ff; color: #004085; }
.type-g703 { background: #e2e3e5; color: #383d41; }
.type-lien { background: #fff3cd; color: #856404; }
.type-sub { background: #f8d7da; color: #721c24; }
.type-pdf { background: #f1f3f4; color: #5f6368; }
.type-default { background: #e9ecef; color: #495057; }

.file-list {
  max-height: 120px;
  overflow-y: auto;
}

.file-item {
  font-size: 12px;
  margin-bottom: 2px;
  color: #666;
}

.continue-section {
  text-align: center;
  margin-top: 24px;
}

.template-setup-required {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.setup-status h5 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
}

.setup-status p {
  margin: 0 0 15px 0;
  color: #666;
}

.setup-progress {
  margin: 15px 0;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.progress-stats {
  display: flex;
  justify-content: space-around;
  gap: 20px;
}

.stat {
  text-align: center;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  display: block;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}

.continue-options {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin: 15px 0;
}

.skip-remaining-btn {
  background: #6c757d;
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s ease;
}

.skip-remaining-btn:hover {
  background: #5a6268;
}

.help-text {
  margin: 15px 0 0 0;
  font-size: 14px;
  color: #666;
  text-align: center;
}

.setup-help-text {
  margin: 0;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.continue-btn {
  background: #4A5FD5;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}

.continue-btn:hover {
  background: #3a4fb5;
}
</style> 